/**
 * pages/signup/steps/Ssn.tsx — SSN entry + submit.
 *
 * CRITICAL: the raw SSN is held in LOCAL COMPONENT STATE ONLY. It is
 * never written to sessionStorage — the retention seam
 * `stripBannedFields` would strip it anyway, but we don't even hand
 * it there. On unmount, React drops the state and the value is gone.
 *
 * The SSN screen's Continue button is the COMMITMENT. Same collapse
 * pattern as Top up and Send: no separate review step; the moment
 * the user hits Continue we call /v1/identity/submit and route based
 * on the returned verdict.
 *
 * The display masks the entered digits after the first-4 are shown —
 * shoulder-surf defence, not screenshot defence.
 */

import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button, Field } from "../../../components";
import {
  isValidSsn,
  normaliseSsn,
  onboardingErrorCopy,
  type IdentityStatus,
} from "@aster/onboarding";
import { submitIdentity, type IdentitySubmitIn } from "../../../api/cip";
import { ApiClientError } from "../../../api/client";
import { getProgress, setProgress, clearProgress } from "../signup-store";
import { advance } from "@aster/onboarding";
import { SignupShell } from "../SignupShell";

const STATUS_TO_ROUTE: Record<IdentityStatus, string> = {
  verified: "/",
  verifying: "/signup/verifying",
  pending: "/signup/pending",
  action_needed: "/signup/documents",
  rejected: "/signup/rejected",
  blocked: "/signup/blocked",
  // A submit that returns `unverified` is a server bug — nothing to
  // route to except back to the transitional screen which will
  // re-fetch and recover.
  unverified: "/signup/verifying",
};

export function SignupSsn() {
  const navigate = useNavigate();
  const cur = getProgress();

  if (!cur || !cur.address || !cur.legal_name || !cur.date_of_birth) {
    return <Navigate to="/signup/address" replace />;
  }

  const [ssn, setSsn] = useState<string>("");   // local only — never persisted
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const digits = normaliseSsn(ssn);
  const isValid = isValidSsn(digits);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isValid || pending) return;
    setPending(true);
    setErr(null);

    // Compose the body — after this, `submitIdentity` posts to the
    // server and we never see the full SSN again. `digits` is a
    // stack-local const; scoping guarantees it doesn't persist.
    const body: IdentitySubmitIn = {
      legal_name: cur.legal_name!,
      dob: cur.date_of_birth!,
      address_line1: cur.address!.line1,
      address_line2: cur.address!.line2 ?? null,
      city: cur.address!.city,
      region: cur.address!.state,
      postal_code: cur.address!.postal_code,
      country: "US",
      ssn: digits,
    };

    try {
      // First move the persisted progress to `verifying` so a
      // mid-request refresh lands the user on the spinner, not back
      // on the SSN screen.
      setProgress(advance(cur, "verifying"));
      const result = await submitIdentity(body);

      // Hosted-flow providers (Plaid IDV) return an `action_url` for
      // the browser to open. Redirect immediately — the user
      // completes the flow in Plaid's hosted UI and returns to
      // /signup/verifying where the poller catches the terminal
      // verdict. The verifying page's redirect target (`redirect_url`
      // configured in the Plaid dashboard template) MUST point back
      // at /signup/verifying so the user lands on the spinner.
      if (result.action_url) {
        // Full-page redirect (not window.open) so the Plaid page has
        // full control of the tab; back button naturally returns to
        // the signup flow.
        window.location.assign(result.action_url);
        return;
      }

      // Response has the verdict. Route based on wire status.
      if (result.status === "verified") {
        clearProgress();
        // Home lives at "/" and is protected. It will call GET /me
        // and render the app shell.
        navigate("/");
        return;
      }
      if (result.status === "blocked") {
        // Terminal — blocked screen wipes progress on mount.
        navigate("/signup/blocked", { state: { reason: "server" } });
        return;
      }
      // pending, action_needed, rejected, verifying — route
      // via the status map.
      navigate(STATUS_TO_ROUTE[result.status]);
    } catch (e: unknown) {
      // Restore the ssn step so the user can retry — the verifying
      // step would leave them stuck on a spinner otherwise.
      setProgress(advance(cur, "ssn"));
      if (e instanceof ApiClientError) {
        if (e.code === "legal_not_accepted") {
          navigate("/signup/legal");
          return;
        }
        setErr(onboardingErrorCopy({ code: e.code, message: e.message }));
      } else {
        setErr("Can't reach Aster right now. Check your connection.");
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <SignupShell
      title="Enter your Social Security number"
      subtitle="Required by federal law to verify identity. We use it once for verification — the full number is never stored on our servers."
      step={5}
      backTo="/signup/address"
      errorBanner={err}
    >
      <form onSubmit={onSubmit}>
        <Field
          label="Social Security number"
          value={ssn}
          onChange={setSsn}
          type="password"
          inputMode="numeric"
          maxLength={11}     // 9 digits + 2 hyphens
          autoComplete="off"
          placeholder="•••-••-••••"
          helper="Only the last 4 digits are stored, and only for identity matching."
        />
        <Button type="submit" disabled={!isValid || pending}>
          {pending ? "Verifying…" : "Verify my identity"}
        </Button>
      </form>
    </SignupShell>
  );
}
